import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Form, InputLabel, Input, Button, Message, SelectLabel, Modal, SelectHierarchyLabel, ToastContext } from '@weezevent/nacre';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import useAppContext from '../../../../../context/useAppContext';
import { UploadDropZone } from '../../../../../nacre/uploadButton';
import { UploadPreviewFile } from '../../../../../components/upload/components/uploadPreview';
import { Inventory } from '../../../../../models';

export default function UpdateZoneModal({ isOpen, onClose, categories, contingents, chartId, zone, synczones, inputError, inputValidity = () => {} }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const [submitting, setSubmitting] = React.useState(false);
    const [fileRejected, setFileRejected] = React.useState(null);
    const [updateForm, setUpdateForm] = useForm({
        access: '',
        contingent: null,
        category: null,
        propagationMode: 0,
        selectedInventories: [],
        file: null
    });

    const [inventories] = Inventory.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            launch: isOpen
        },
        [organization, chartId, isOpen]
    );

    const activeInventories = React.useMemo(() => {
        return inventories?.filter(iv => iv.active && (iv.end_date === null || iv.end_date < moment().toISOString())).map(i => i.uuid);
    }, [inventories]);

    const inventoriesOptions = React.useMemo(() => {
        return inventories
            ?.filter(iv => iv.active)
            .map(iv => ({
                text: iv.name,
                value: iv.uuid
            }));
    }, [inventories]);

    const propagationOptions = React.useMemo(() => {
        return [
            { text: t('ticket.charts.zone.propagate.options.all'), value: 0 },
            { text: t('ticket.charts.zone.propagate.options.active'), value: 1 },
            { text: t('ticket.charts.zone.propagate.options.manual'), value: 2 }
        ];
    }, []);

    const seatMapError = React.useMemo(() => {
        if (!fileRejected) {
            return null;
        }
        return (
            <Message className="file-error-message" title={t('nacre.ImportController.analysis.header.error')} type="danger">
                {t('ticket.charts.newZone.fileError.seatMap', { format: '.xlsx' })}
            </Message>
        );
    }, [fileRejected]);

    const handleFileUpload = React.useCallback(
        (files, rejected) => {
            if (rejected.length) {
                setFileRejected(rejected[0]);
                setUpdateForm('file', null);
                return;
            }
            setFileRejected(null);
            setUpdateForm('file', files[0]);
        },
        [setUpdateForm]
    );

    const disableSubmit = React.useMemo(() => {
        return !updateForm.category || !updateForm.file || !updateForm.access || !inputValidity(updateForm.access);
    }, [updateForm, inputValidity]);

    const manualSelection = React.useMemo(() => {
        return updateForm.propagationMode === 2;
    }, [updateForm, updateForm.propagationMode]);

    const categoriesOptions = React.useMemo(() => {
        if (!categories) return [];

        return categories?.map(cat => ({
            text: cat.name,
            value: cat.uuid
        }));
    }, [categories]);

    const contingentsOptions = React.useMemo(() => {
        if (!contingents) return [];

        return contingents?.map(contingent => ({
            text: contingent.name,
            value: contingent.uuid
        }));
    }, [contingents]);

    const inventoriesUuids = React.useMemo(() => {
        if (!inventories?.length) return [];
        return inventories.map(i => i.uuid);
    }, [inventories]);

    const propagateToInventories = React.useMemo(() => {
        switch (updateForm.propagationMode) {
            case 1:
                return activeInventories;
            case 2:
                return updateForm.selectedInventories;
            case 0:
            default:
                return inventoriesUuids;
        }
    }, [updateForm.propagationMode, updateForm.selectedInventories, activeInventories, inventoriesUuids]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        const { file, access, category, contingent } = updateForm;

        const formData = new FormData();

        // Required fields
        formData.append('file', file);
        formData.append('access', access);
        formData.append('name', zone.name);
        formData.append('category', category);
        if (contingent) formData.append('contingent', contingent);

        propagateToInventories.forEach((uuid, index) => {
            formData.append(`propagate_to_inventories[${index}]`, uuid);
        });

        fetcher
            .put(`/ticket/organizations/${organization.id}/charts/${chartId}/zones/${zone.pk}`, {
                data: formData
            })
            .then(() => {
                toast.success(t('ticket.charts.newZone.editSuccess'));
                synczones();
                onClose();
            })
            .catch(error => {
                console.error(error);
                toast.error(t('ticket.charts.newZone.editFailure'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [organization.id, chartId, zone, updateForm, inventoriesUuids, activeInventories, onClose, propagateToInventories, synczones]);

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size="large"
            title={t('ticket.charts.newZone.update.title')}
            submitButton={
                <Button onClick={handleSubmit} primary disabled={disableSubmit || submitting}>
                    {t('ticket.common.action.save')}
                </Button>
            }
            cancelButton={
                <Button onClick={onClose} secondary>
                    {t('ticket.common.action.cancel')}
                </Button>
            }
        >
            <Message type="default" title={t('ticket.charts.newZone.update.info.title')}>
                <p>{t('ticket.charts.newZone.update.info.newSeatsOnly')}</p>
                <p>{t('ticket.charts.newZone.update.info.deletionRule')}</p>
                <p>{t('ticket.charts.newZone.update.info.seatPreservation')}</p>
            </Message>

            <Form>
                <Form.Field>
                    <InputLabel label={t('ticket.charts.newZone.access')} />
                    <Input
                        value={updateForm.access}
                        onChange={({ target: { value } }) => setUpdateForm('access', value.trim())}
                        placeholder={t('ticket.charts.newZone.placeholder')}
                        errors={Boolean(!inputValidity(updateForm.access))}
                    />
                    <Form.FieldErrors errors={[inputError(updateForm.access)]} />
                </Form.Field>
                <Form.Field>
                    <SelectLabel
                        label={t('ticket.common.seating.contingent')}
                        value={updateForm.contingent}
                        options={contingentsOptions}
                        placeholder={t('ticket.rates.contingent-placeholder')}
                        onChange={value => setUpdateForm('contingent', value)}
                        onClear={() => setUpdateForm('contingent', null)}
                        useNew
                    />
                </Form.Field>
                <Form.Field>
                    <SelectLabel
                        label={t('ticket.common.seating.category')}
                        value={updateForm.category}
                        options={categoriesOptions}
                        placeholder={t('ticket.rates.category-placeholder')}
                        onChange={value => setUpdateForm('category', value)}
                        onClear={() => setUpdateForm('category', null)}
                        useNew
                    />
                </Form.Field>

                <SelectLabel
                    label={t('ticket.common.inventories')}
                    value={updateForm.propagationMode}
                    options={propagationOptions}
                    onChange={value => setUpdateForm('propagationMode', value)}
                    disabled={!inventories?.length}
                    useNew
                />

                {manualSelection && (
                    <SelectHierarchyLabel
                        multi
                        height="auto"
                        searchable={false}
                        placeholder={t('ticket.common.action.select')}
                        options={inventoriesOptions}
                        value={updateForm.selectedInventories.length ? t('ticket.charts.chart.inventories_selected', { count: updateForm.selectedInventories.length }) : null}
                        selected={updateForm.selectedInventories}
                        label={t('ticket.common.inventories')}
                        onChange={selected => setUpdateForm('selectedInventories', selected)}
                    />
                )}

                <Form.Field>
                    <Form.Label label={t('ticket.charts.newZone.update.excelFile')} />
                    {updateForm.file ? (
                        <div className="file-input-block">
                            <UploadPreviewFile onDelete={() => setUpdateForm('file', null)} fileSrc={updateForm.file?.path} isUploadedS3={false} displayDate={false} />
                        </div>
                    ) : (
                        <UploadDropZone acceptedFileExtensions={['.xlsx']} onFileSelected={handleFileUpload} label={t('ticket.charts.newZone.uploadLabel')} />
                    )}
                    {seatMapError}
                </Form.Field>
            </Form>
        </Modal>
    );
}
